import call from "./call";
import {
  VoicesCampaign,
  VoicesCampaignImportResult,
  VoicesCampaignPayload,
  VoicesFeed,
  VoicesFeedCheck,
  VoicesFeedPayload,
} from "../Models/voicesCampaigns";
import { saveDownload } from "../Helpers/voicesDownload";
import { voicesHeaders } from "../Helpers/voicesWorkspace";

export const getVoicesCampaigns = async (): Promise<VoicesCampaign[]> => {
  const response = await call.get<{ campaigns: VoicesCampaign[] }>(
    "/v1/voices/campaigns/",
    { headers: voicesHeaders(), snack: { error: "voices.campaignsFetchError" } },
  );
  return response.data.campaigns;
};

export const getVoicesCampaign = async (
  campaignId: number,
): Promise<VoicesCampaign> => {
  const response = await call.get<VoicesCampaign>(
    `/v1/voices/campaigns/${campaignId}/`,
    { headers: voicesHeaders(), snack: { error: "voices.campaignFetchError" } },
  );
  return response.data;
};

export const createVoicesCampaign = async (
  data: VoicesCampaignPayload,
): Promise<VoicesCampaign> => {
  const response = await call.post<VoicesCampaign>(
    "/v1/voices/campaigns/",
    data,
    {
      headers: voicesHeaders(),
      snack: {
        success: "voices.campaignCreateSuccess",
        error: "voices.campaignSaveError",
      },
    },
  );
  return response.data;
};

export const updateVoicesCampaign = async (
  campaignId: number,
  data: VoicesCampaignPayload,
): Promise<VoicesCampaign> => {
  const response = await call.put<VoicesCampaign>(
    `/v1/voices/campaigns/${campaignId}/`,
    data,
    {
      headers: voicesHeaders(),
      snack: {
        success: "voices.campaignUpdateSuccess",
        error: "voices.campaignSaveError",
      },
    },
  );
  return response.data;
};

export const closeVoicesCampaign = async (
  campaignId: number,
): Promise<VoicesCampaign> => {
  const response = await call.post<VoicesCampaign>(
    `/v1/voices/campaigns/${campaignId}/close/`,
    {},
    {
      headers: voicesHeaders(),
      snack: {
        success: "voices.campaignCloseSuccess",
        error: "voices.campaignCloseError",
      },
    },
  );
  return response.data;
};

export const deleteVoicesCampaign = async (campaignId: number): Promise<void> => {
  await call.delete(`/v1/voices/campaigns/${campaignId}/`, {
    headers: voicesHeaders(),
    snack: {
      success: "voices.campaignDeleteSuccess",
      error: "voices.campaignDeleteError",
    },
  });
};

export const createVoicesFeed = async (
  campaignId: number,
  data: VoicesFeedPayload,
): Promise<VoicesFeed> => {
  const response = await call.post<VoicesFeed>(
    `/v1/voices/campaigns/${campaignId}/feeds/`,
    data,
    {
      headers: voicesHeaders(),
      snack: { success: "voices.feedSaveSuccess", error: "voices.feedSaveError" },
    },
  );
  return response.data;
};

export const updateVoicesFeed = async (
  campaignId: number,
  feedId: number,
  data: Partial<VoicesFeedPayload>,
): Promise<VoicesFeed> => {
  const response = await call.put<VoicesFeed>(
    `/v1/voices/campaigns/${campaignId}/feeds/${feedId}/`,
    data,
    {
      headers: voicesHeaders(),
      snack: { success: "voices.feedSaveSuccess", error: "voices.feedSaveError" },
    },
  );
  return response.data;
};

export const deleteVoicesFeed = async (
  campaignId: number,
  feedId: number,
): Promise<void> => {
  await call.delete(`/v1/voices/campaigns/${campaignId}/feeds/${feedId}/`, {
    headers: voicesHeaders(),
    snack: {
      success: "voices.feedDeleteSuccess",
      error: "voices.feedDeleteError",
    },
  });
};

export const checkVoicesFeed = async (
  campaignId: number,
  feedId: number,
): Promise<VoicesFeedCheck> => {
  const response = await call.get<VoicesFeedCheck>(
    `/v1/voices/campaigns/${campaignId}/feeds/${feedId}/check/`,
    { headers: voicesHeaders(), snack: { error: "voices.feedCheckError" } },
  );
  return response.data;
};

export const exportVoicesCampaign = async (campaignId: number): Promise<void> => {
  const response = await call.get<Blob>(`/v1/voices/campaigns/${campaignId}/export/`, {
    headers: voicesHeaders(),
    responseType: "blob",
    snack: { error: "voices.campaignExportError" },
  });
  saveDownload(response, "voices-campaigns.json");
};

export const importVoicesCampaigns = async (
  document: unknown,
  confirm: boolean,
): Promise<VoicesCampaignImportResult> => {
  const response = await call.post<VoicesCampaignImportResult>(
    "/v1/voices/campaigns/import/",
    { document, confirm },
    {
      headers: voicesHeaders(),
      snack: {
        error: "voices.campaignImportError",
        400: "voices.campaignImportInvalid",
        ...(confirm ? { success: "voices.campaignImportSuccess" } : {}),
      },
    },
  );
  return response.data;
};
